import React, { useEffect, useRef } from 'react';
import Typed from 'typed.js';
import { Particles } from "react-tsparticles";
import { loadFull } from "tsparticles";
import type { Engine } from "tsparticles-engine";
import { useTranslation } from 'react-i18next';

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const typedRef = useRef<HTMLSpanElement>(null);
  const typed = useRef<Typed | null>(null);
  const cursorRef = useRef<HTMLDivElement>(null);

  const particlesInit = async (engine: Engine) => {
    await loadFull(engine);
  };

  useEffect(() => {
    if (typedRef.current) {
      const typedStrings = t('hero.typed', { returnObjects: true });
      const strings = Array.isArray(typedStrings) ? typedStrings : ['Algorithmic', 'Quantified', 'Autonomous'];
      
      typed.current = new Typed(typedRef.current, {
        strings,
        typeSpeed: 80,
        backSpeed: 60,
        backDelay: 1000,
        startDelay: 300,
        loop: true,
        showCursor: true,
        cursorChar: '|',
      });
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      if (typed.current) {
        typed.current.destroy();
      }
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [t]);

  return (
    <section className="relative min-h-screen flex items-center pt-20 bg-hero-gradient overflow-hidden cursor-wave">
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false, zIndex: -1 },
          particles: {
            number: { value: 60 },
            color: { value: "#36E8A0" },
            shape: { type: "circle" },
            opacity: {
              value: 0.3,
              random: true,
              anim: { enable: true, speed: 1, opacity_min: 0.1, sync: false }
            },
            size: {
              value: { min: 1, max: 3 },
              anim: { enable: true, speed: 2, size_min: 0.5, sync: false }
            },
            move: {
              enable: true,
              speed: 1,
              direction: "none",
              random: false,
              straight: false,
              outModes: "out",
              bounce: false,
              attract: { enable: false, rotateX: 600, rotateY: 1200 }
            },
            links: {
              enable: true,
              distance: 150,
              color: "#36E8A0",
              opacity: 0.2,
              width: 1
            }
          },
          interactivity: {
            detect_on: "window",
            events: {
              onHover: { enable: true, mode: "grab" },
            },
            modes: {
              grab: { distance: 140, links: { opacity: 0.3 } }
            }
          },
          retina_detect: true,
          background: {
            color: "transparent"
          }
        }}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0
        }}
      />
      
      {/* Cyber grid background */}
      <div className="absolute inset-0 cyber-grid"></div>
      
      {/* Particle network overlay */}
      <div className="absolute inset-0 particle-net"></div>
      
      {/* Custom cursor effect */}
      <div ref={cursorRef} className="fixed pointer-events-none z-50"></div>
      
      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="mb-6 leading-tight animate-fadeIn flex flex-col sm:flex-row items-center justify-center">
            <div className="min-w-[280px] sm:min-w-0">
              <span ref={typedRef} className="text-5xl sm:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400 inline-block"></span>
            </div>
            <span className="text-5xl sm:text-6xl font-extrabold tracking-tight text-white sm:ml-2">
              {t('hero.title')}
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-text-secondary mb-10 max-w-2xl mx-auto leading-relaxed" style={{animationDelay: '0.2s'}}>
            {t('hero.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16" style={{animationDelay: '0.4s'}}>
            <a href="#copytrading" className="btn btn-accent animate-glowPulse">
              {t('hero.cta.primary')}
            </a>
            <a href="#results" className="btn btn-outline">
              {t('hero.cta.secondary')}
            </a>
          </div>
        </div>
        
        {/* Soft blue glow edges */}
        <div className="absolute -bottom-80 left-1/2 transform -translate-x-1/2 w-full max-w-4xl h-80 rounded-full bg-gradient-from/20 blur-[100px] opacity-50"></div>
      </div>
    </section>
  );
};

export default Hero;